import type { ComponentType } from "react"
import { lazy, Suspense, useEffect, useState } from "react"

import type { AssigneeSelectProps } from "./assignee-select"

// Lazy load the AssigneeSelect component
const AssigneeSelectLazy = lazy(() =>
	import("./assignee-select").then((module) => ({
		default: module.AssigneeSelect as ComponentType<AssigneeSelectProps>,
	})),
)

// Simple preloader component - renders children directly without wrapper to avoid layout shift
const AssigneeSelectPreloader = ({
	children,
}: Pick<AssigneeSelectProps, "children">) => {
	if (children) {
		return <>{children}</>
	}
	return (
		<div className="flex items-center gap-2 px-3 py-2 opacity-50">
			<div className="h-4 w-4 animate-pulse rounded-full bg-gray-300" />
			<div className="h-4 w-16 animate-pulse rounded bg-gray-300" />
		</div>
	)
}

export const LazyAssigneeSelect = (props: AssigneeSelectProps) => {
	const [shouldLoad, setShouldLoad] = useState(false)

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setShouldLoad(true)
		}, 0)

		return () => {
			clearTimeout(timeoutId)
		}
	}, [])

	// Show preloader until browser is idle
	if (!shouldLoad) {
		return <AssigneeSelectPreloader>{props.children}</AssigneeSelectPreloader>
	}

	// Once idle, render the lazy-loaded component with Suspense
	return (
		<Suspense
			fallback={
				<AssigneeSelectPreloader>{props.children}</AssigneeSelectPreloader>
			}
		>
			<AssigneeSelectLazy {...props} />
		</Suspense>
	)
}
